import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ChevronRight, LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface SettingItem {
  name: string;
  tab: string;
  icon: LucideIcon;
  description: string;
  badge?: string | number;
  isNew?: boolean;
  isBeta?: boolean;
}

interface SettingGroup {
  title: string;
  icon: LucideIcon;
  items: SettingItem[];
}

interface MobileSettingsCardProps {
  groups: SettingGroup[];
  activeTab: string;
}

const MobileSettingsCard = ({ groups, activeTab }: MobileSettingsCardProps) => {
  // Find which group contains the active tab
  const activeGroupIndex = groups.findIndex(group =>
    group.items.some(item => item.tab === activeTab)
  );

  // Default to first group if none active
  const defaultValue = activeGroupIndex >= 0 ? `group-${activeGroupIndex}` : 'group-0';

  return (
    <Card className="bg-card/60 backdrop-blur-xl border-border/50 overflow-hidden">
      <Accordion type="single" collapsible defaultValue={defaultValue} className="w-full">
        {groups.map((group, groupIndex) => {
          const GroupIcon = group.icon;
          const hasActiveItem = group.items.some(item => item.tab === activeTab);

          return (
            <AccordionItem
              key={groupIndex}
              value={`group-${groupIndex}`}
              className="border-b border-border/50 last:border-0"
            >
              <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                    hasActiveItem 
                      ? "bg-primary/20 text-primary" 
                      : "bg-muted text-muted-foreground"
                  )}>
                    <GroupIcon className="h-5 w-5" />
                  </div>
                  <span className={cn(
                    "font-semibold text-base",
                    hasActiveItem && "text-primary"
                  )}>
                    {group.title}
                  </span>
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-2 pb-2">
                <div className="space-y-1">
                  {group.items.map((item, itemIndex) => {
                    const ItemIcon = item.icon;
                    const isActive = item.tab === activeTab;

                    return (
                      <motion.div
                        key={item.tab}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: itemIndex * 0.05 }}
                      >
                        <Link
                          to={`/settings?tab=${item.tab}`}
                          className={cn(
                            "flex items-center gap-3 px-4 py-3.5 rounded-lg transition-all",
                            "min-h-[56px] touch-manipulation", // WCAG AAA touch target
                            isActive
                              ? "bg-primary/10 text-primary"
                              : "hover:bg-muted/50 text-foreground"
                          )}
                        >
                          <ItemIcon className={cn(
                            "h-5 w-5 shrink-0",
                            isActive ? "text-primary" : "text-muted-foreground"
                          )} />
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">
                                {item.name}
                              </span>
                              {item.isNew && (
                                <Badge variant="secondary" className="bg-primary/10 text-primary text-xs px-1.5 py-0">
                                  New
                                </Badge>
                              )}
                              {item.isBeta && (
                                <Badge variant="secondary" className="bg-secondary/10 text-secondary text-xs px-1.5 py-0">
                                  Beta
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {item.description}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {item.badge && (
                              <Badge variant="secondary" className="bg-primary/10 text-primary">
                                {item.badge}
                              </Badge>
                            )}
                            <ChevronRight className={cn(
                              "h-4 w-4 transition-colors",
                              isActive ? "text-primary" : "text-muted-foreground"
                            )} />
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Card>
  );
};

export default MobileSettingsCard;
